import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DigestMessage,
  DigestSettings,
  getDigestSettings,
  previewDigest,
  sendDigest,
  setDigestWebhook,
} from '../../services/Digest';

/** Slack mrkdwn → plain text good enough for a preview */
const plain = (s: string) =>
  s
    .replace(/<([^|>]+)\|([^>]+)>/g, '$2')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/:warning:/g, '⚠');

/**
 * Staff: the morning Slack digest of how each customer used Queri. Paste
 * any Slack incoming webhook (its channel is where the digest goes).
 */
const DigestCard: React.FC = () => {
  const [settings, setSettings] = useState<DigestSettings | null>(null);
  const [webhook, setWebhook] = useState('');
  const [preview, setPreview] = useState<DigestMessage | null>(null);
  const [busy, setBusy] = useState<'save' | 'preview' | 'send' | null>(null);

  useEffect(() => {
    getDigestSettings().then((res) => res.success && res.data && setSettings(res.data));
  }, []);

  const error = (res: { errors?: { detail?: string; title?: string }[] }, fallback: string) =>
    toast.error(res.errors?.[0]?.detail || res.errors?.[0]?.title || fallback);

  const save = async (url: string | null) => {
    setBusy('save');
    const res = await setDigestWebhook(url);
    setBusy(null);
    if (res.success && res.data) {
      setSettings(res.data);
      setWebhook('');
      toast.success(url ? 'Saved. The digest posts every morning at 07:00.' : 'Webhook removed');
    } else error(res, 'Could not save the webhook');
  };

  const showPreview = async () => {
    setBusy('preview');
    const res = await previewDigest();
    setBusy(null);
    if (res.success && res.data) setPreview(res.data);
    else error(res, 'Could not build the digest');
  };

  const send = async () => {
    setBusy('send');
    const res = await sendDigest();
    setBusy(null);
    if (res.success && res.data) {
      setPreview(res.data);
      toast.success('Posted to Slack');
    } else error(res, 'Could not post to Slack');
  };

  return (
    <Card className="mt-6 space-y-4 p-6">
      <div>
        <h2 className="text-lg font-semibold">Daily digest</h2>
        <p className="text-sm text-muted-foreground">
          Every morning at 07:00 (Stockholm), a Slack summary of how each customer used Queri the day before: who was
          active, what they did, what failed, cost against the ceiling, and who has gone quiet. Staff accounts are left
          out.
        </p>
      </div>
      {settings && (
        <div className="text-sm">
          {settings.enabled ? (
            <>
              Posting to <span className="font-mono text-xs">{settings.webhookHint}</span>
              <span className="text-muted-foreground">
                {settings.source === 'environment'
                  ? ' (from SLACK_DIGEST_WEBHOOK_URL)'
                  : settings.updatedBy && ` (set by ${settings.updatedBy})`}
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">Off: no Slack webhook set.</span>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Input
          type="password"
          autoComplete="off"
          placeholder="https://hooks.slack.com/services/…"
          value={webhook}
          onChange={(e) => setWebhook(e.target.value)}
          className="max-w-md flex-1"
        />
        <Button disabled={!webhook.trim() || !!busy} onClick={() => save(webhook.trim())}>
          {busy === 'save' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {settings?.source === 'admin' ? 'Replace webhook' : 'Save webhook'}
        </Button>
        {settings?.source === 'admin' && (
          <Button variant="ghost" disabled={!!busy} onClick={() => save(null)}>
            Remove
          </Button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" disabled={!!busy} onClick={showPreview}>
          {busy === 'preview' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Preview yesterday
        </Button>
        <Button variant="outline" disabled={!settings?.enabled || !!busy} onClick={send}>
          {busy === 'send' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Post to Slack now
        </Button>
      </div>
      {preview && (
        <div className="space-y-3 rounded-md border bg-muted/40 p-4 text-sm">
          {preview.blocks.map((b, i) => (
            <div key={i} className={b.type === 'header' ? 'font-semibold' : 'whitespace-pre-wrap'}>
              {plain(b.text.text)}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default DigestCard;
